import * as Scheme from '@/models/api/paid-member-price/member-plan/scheme'

export type AvailableMembershipPlan = SubscribedPaidMembershipPlan
  | UnsubscribedPaidMembershipPlan
  | ReservedPaidMembershipPlan
  | HoldingPaidMembershipPlan
  | NotEverSubscribedPaidMembershipPlan

abstract class BaseMembershipPlan<R extends Scheme.AvailablePaidMembershipPlanScheme> {
  constructor(protected readonly record: R) {
  }

  get displayLabel(): string {
    return this.record.display_label
  }
  get externalId(): string {
    return this.record.external_id
  }

  /**
   * @see BasePaidMemberPlan.active
   */
  isActive(): boolean {
    return this.record.active
  }

  abstract subscribed(): this is SubscribedPaidMembershipPlan
  abstract unsubscribed(): this is UnsubscribedPaidMembershipPlan
  abstract notEverSubscribed(): this is NotEverSubscribedPaidMembershipPlan
  abstract reserved(): this is ReservedPaidMembershipPlan
  abstract holding(): this is HoldingPaidMembershipPlan
}

export interface SubscribingPeriod {
  readonly begin: Date
  readonly end: Date
}

/**
 * 現在契約中の有料会員プラン
 */
export class SubscribedPaidMembershipPlan
  extends BaseMembershipPlan<Scheme.SubscribedAvailablePaidMembershipPlanScheme> {
  get subscribingPeriod(): SubscribingPeriod {
    return {
      begin: new Date(this.record.subscribing_period.begin),
      end: new Date(this.record.subscribing_period.end),
    }
  }

  get subscribeId(): string {
    return this.record.subscribe_id
  }

  subscribed(): this is SubscribedPaidMembershipPlan {
    return true
  }
  unsubscribed(): this is UnsubscribedPaidMembershipPlan {
    return false
  }
  notEverSubscribed(): this is NotEverSubscribedPaidMembershipPlan {
    return false
  }
  reserved(): this is ReservedPaidMembershipPlan {
    return false
  }
  holding(): this is HoldingPaidMembershipPlan {
    return false
  }
}
/**
 * 解約された有料会員プラン
 */
export class UnsubscribedPaidMembershipPlan
  extends BaseMembershipPlan<Scheme.UnsubscribedAvailablePaidMembershipPlanScheme> {
  get subscribingPeriod(): SubscribingPeriod {
    return {
      begin: new Date(this.record.subscribing_period.begin),
      end: new Date(this.record.subscribing_period.end),
    }
  }

  get unsubscribedAt(): Date {
    return new Date(this.record.unsubscribed_at)
  }
  get subscribeId(): string {
    return this.record.subscribe_id
  }

  subscribed(): this is SubscribedPaidMembershipPlan {
    return false
  }
  unsubscribed(): this is UnsubscribedPaidMembershipPlan {
    return true
  }
  notEverSubscribed(): this is NotEverSubscribedPaidMembershipPlan {
    return false
  }
  reserved(): this is ReservedPaidMembershipPlan {
    return false
  }
  holding(): this is HoldingPaidMembershipPlan {
    return false
  }
}

export class ReservedPaidMembershipPlan
  extends BaseMembershipPlan<Scheme.ReservedAvailablePaidMembershipPlanScheme> {
  get subscribingPeriod(): SubscribingPeriod {
    return {
      begin: new Date(this.record.subscribing_period.begin),
      end: new Date(this.record.subscribing_period.end),
    }
  }
  get subscribeId(): string {
    return this.record.subscribe_id
  }

  subscribed(): this is SubscribedPaidMembershipPlan {
    return this.record.subscribed
  }
  unsubscribed(): this is UnsubscribedPaidMembershipPlan {
    return false
  }
  notEverSubscribed(): this is NotEverSubscribedPaidMembershipPlan {
    return false
  }
  reserved(): this is ReservedPaidMembershipPlan {
    return true
  }
  holding(): this is HoldingPaidMembershipPlan {
    return false
  }
}

/**
 * 保留中ステータスのプラン.
 */
export class HoldingPaidMembershipPlan extends BaseMembershipPlan<Scheme.HoldingPaidMembershipPlanScheme> {
  get subscribeId(): string {
    return this.record.subscribe_id
  }

  notEverSubscribed(): this is NotEverSubscribedPaidMembershipPlan {
    return false
  }
  reserved(): this is ReservedPaidMembershipPlan {
    return false
  }
  subscribed(): this is SubscribedPaidMembershipPlan {
    return false
  }
  unsubscribed(): this is UnsubscribedPaidMembershipPlan {
    return false
  }
  holding(): this is HoldingPaidMembershipPlan {
    return true
  }
}

/**
 * 一度も契約されたことのない有料会員プラン
 */
export class NotEverSubscribedPaidMembershipPlan
  extends BaseMembershipPlan<Scheme.NotEverSubscribedAvailablePaidMembershipPlanScheme> {
  readonly subscribeId: null = null
  readonly subscribingPeriod: null = null
  readonly unsubscribedAt: null = null

  subscribed(): this is SubscribedPaidMembershipPlan {
    return false
  }
  unsubscribed(): this is UnsubscribedPaidMembershipPlan {
    return false
  }
  notEverSubscribed(): this is NotEverSubscribedPaidMembershipPlan {
    return true
  }
  reserved(): this is ReservedPaidMembershipPlan {
    return false
  }
  holding(): this is HoldingPaidMembershipPlan {
    return false
  }
}
